import React, { useState, useEffect } from "react";
import "./VacationSchedule.css";
import { getUsersSummary, getAllDaysOff } from "../../../../../api/api";
import { getHrInspectorReports } from "../../../../../api/reportsApi";

const VacationSchedule = () => {
  const [year, setYear] = useState(2025);
  const [department, setDepartment] = useState("Все отделы");
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyOnVacation, setShowOnlyOnVacation] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vacationData, setVacationData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Форматирование даты в строку
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Загрузка данных сотрудников
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await getUsersSummary({
          isActive: true,
          pageSize: 1000,
        });

        // Получаем уникальные отделы
        const uniqueDepartments = [
          ...new Set(
            response.users.map((user) => user.department).filter(Boolean)
          ),
        ];
        setDepartments(uniqueDepartments);

        setAllEmployees(response.users);
      } catch (error) {
        console.error("Ошибка при загрузке сотрудников:", error);
      }
    };

    loadEmployees();
  }, []);

  // Загрузка данных об отпусках
  useEffect(() => {
    const loadVacationData = async () => {
      try {
        setLoading(true);

        // Получаем отчеты об отпусках
        let page = 1;
        const pageSize = 100;
        let allReports = [];
        let hasMore = true;

        while (hasMore) {
          const response = await getHrInspectorReports({
            page,
            page_size: pageSize,
          });
          allReports = allReports.concat(response.reports);
          if (!response.reports || response.reports.length < pageSize) {
            hasMore = false;
          } else {
            page += 1;
          }
        }

        // Фильтруем отпуска по выбранному году
        const yearReports = allReports.filter((report) => {
          const reportYear = new Date(report.fact_date_start).getFullYear();
          return (
            reportYear === year && report.portal_job_type_name === "Отпуск"
          );
        });

        // Группируем по пользователю
        const groupedByUser = {};
        yearReports.forEach((report) => {
          const userId = report.user_id;
          if (!groupedByUser[userId]) {
            groupedByUser[userId] = [];
          }
          groupedByUser[userId].push(report);
        });

        // Для каждого пользователя группируем отпуска по периодам
        const processedVacations = [];
        Object.entries(groupedByUser).forEach(([userId, reports]) => {
          // Сортируем по дате
          const sortedReports = reports.sort(
            (a, b) => new Date(a.fact_date_start) - new Date(b.fact_date_start)
          );

          // Группируем в непрерывные периоды
          const vacationPeriods = [];
          let currentPeriod = [];

          sortedReports.forEach((report) => {
            const reportDate = new Date(report.fact_date_start);

            if (currentPeriod.length === 0) {
              currentPeriod.push(report);
            } else {
              const lastDate = new Date(
                currentPeriod[currentPeriod.length - 1].fact_date_start
              );
              const dayDiff = (reportDate - lastDate) / (1000 * 60 * 60 * 24);

              if (dayDiff <= 1) {
                currentPeriod.push(report);
              } else {
                vacationPeriods.push([...currentPeriod]);
                currentPeriod = [report];
              }
            }
          });

          if (currentPeriod.length > 0) {
            vacationPeriods.push(currentPeriod);
          }

          // Добавляем периоды отпусков для пользователя
          vacationPeriods.forEach((period, index) => {
            const startDate = new Date(period[0].fact_date_start);
            const endDate = new Date(period[period.length - 1].fact_date_start);
            const days = period.length;

            processedVacations.push({
              userId: parseInt(userId),
              user: period[0].user,
              department: period[0].group_name,
              vacationIndex: index,
              startDate,
              endDate,
              days,
              period: `${startDate.toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
              })} — ${endDate.toLocaleDateString("ru-RU", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })} (${days} дн.)`,
            });
          });
        });

        setVacationData(processedVacations);
      } catch (error) {
        console.error("Ошибка при загрузке отпусков:", error);
      } finally {
        setLoading(false);
      }
    };

    if (allEmployees.length > 0) {
      loadVacationData();
    }
  }, [year, allEmployees]);

  // Подготовка данных для отображения
  const prepareEmployeesData = () => {
    const employeesWithVacations = allEmployees.map((employee) => {
      const userVacations = vacationData.filter(
        (v) => v.userId === employee.id
      );
      // Сортируем отпуска по дате
      userVacations.sort((a, b) => a.startDate - b.startDate);
      // Формируем строку должности из roles
      let position = "Не указано";
      if (Array.isArray(employee.roles) && employee.roles.length > 0) {
        position = employee.roles.join(" / ");
      } else if (employee.position) {
        position = employee.position;
      }
      return {
        id: employee.id,
        position,
        fullName:
          `${employee.surname} ${employee.name} ${employee.patronymic}`.trim(),
        hireDate: employee.hire_date
          ? new Date(employee.hire_date).toLocaleDateString("ru-RU")
          : "Не указана",
        department: employee.department || "Не указан",
        totalDays: "28/0", // TODO: Подсчитать реальные дни
        firstVacation: userVacations[0]?.period || "-",
        secondVacation: userVacations[1]?.period || "-",
        thirdVacation: userVacations[2]?.period || "-",
        hasVacations: userVacations.length > 0,
      };
    });
    return employeesWithVacations;
  };

  // Фильтрация и группировка данных
  const filteredAndGroupedEmployees = () => {
    const employeesData = prepareEmployeesData();

    // Фильтрация
    const filtered = employeesData.filter((employee) => {
      const matchesSearch =
        employee.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.position.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment =
        department === "Все отделы" || employee.department === department;
      const matchesVacationFilter =
        !showOnlyOnVacation || employee.hasVacations;

      return matchesSearch && matchesDepartment && matchesVacationFilter;
    });

    // Группировка по отделам
    const grouped = {};
    filtered.forEach((employee) => {
      const dept = employee.department;
      if (!grouped[dept]) {
        grouped[dept] = [];
      }
      grouped[dept].push(employee);
    });

    return grouped;
  };

  const groupedEmployees = filteredAndGroupedEmployees();

  return (
    <div className="vacation-schedule">
      <div className="vacation-schedule__header">
        <h1 className="vacation-schedule__title">График отпусков</h1>

        <div className="vacation-schedule__controls">
          <div className="vacation-schedule__filters">
            <div className="vacation-schedule__filter-group">
              <select
                className="vacation-schedule__select"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
              >
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>

              <select
                className="vacation-schedule__select"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              >
                <option value="Все отделы">Все отделы</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            <div className="vacation-schedule__search-group">
              <div className="vacation-schedule__search">
                <input
                  type="text"
                  placeholder="Поиск"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="vacation-schedule__search-input"
                />
                <span className="vacation-schedule__search-icon">🔍</span>
              </div>

              <label className="vacation-schedule__checkbox-label">
                <input
                  type="checkbox"
                  checked={showOnlyOnVacation}
                  onChange={(e) => setShowOnlyOnVacation(e.target.checked)}
                  className="vacation-schedule__checkbox"
                />
                Показать только тех, кто в отпуске
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="vacation-schedule__content">
        {loading ? (
          <div className="vacation-schedule__loading">Загрузка...</div>
        ) : (
          <div className="vacation-schedule__table-container">
            <table className="vacation-schedule__table">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Должность (по штатному расписанию)</th>
                  <th>ФИО</th>
                  <th>Дата приема</th>
                  <th>Количество дней (положенных/факт.)</th>
                  <th>Первая часть отпуска (Обязательная)</th>
                  <th>Вторая часть отпуска</th>
                  <th>Третья часть отпуска</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedEmployees).length === 0 ? (
                  <tr>
                    <td colSpan="8" className="vacation-schedule__no-data">
                      Данные не найдены
                    </td>
                  </tr>
                ) : (
                  Object.entries(groupedEmployees).map(
                    ([deptName, employees], deptIndex) => (
                      <React.Fragment key={deptName}>
                        {/* Строка с названием отдела */}
                        <tr className="vacation-schedule__department-row">
                          <td
                            colSpan="8"
                            className="vacation-schedule__department-title"
                          >
                            {deptName}
                          </td>
                        </tr>

                        {/* Сотрудники отдела */}
                        {employees.map((employee, empIndex) => {
                          const globalIndex =
                            Object.values(groupedEmployees)
                              .slice(0, deptIndex)
                              .reduce((sum, dept) => sum + dept.length, 0) +
                            empIndex +
                            1;

                          return (
                            <tr key={employee.id}>
                              <td>{globalIndex}</td>
                              <td>{employee.position}</td>
                              <td>{employee.fullName}</td>
                              <td>{employee.hireDate}</td>
                              <td>{employee.totalDays}</td>
                              <td>{employee.firstVacation}</td>
                              <td>{employee.secondVacation}</td>
                              <td>{employee.thirdVacation}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    )
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VacationSchedule;
